import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Modal from "./Modal";
import { formatIDR } from "../lib/format";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { PassiveBadge } from "./ui/data-presentation";
import { FormError } from "./ui/feedback";

/**
 * Two-step CSV import dialog:
 * 1. Select file → backend returns preview rows with duplicate info.
 * 2. User picks per-row action (add / replace / skip) for duplicates, then applies.
 */
export default function ImportDialog({ open, onClose, warehouses, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, warehouse_codes }
  const [rowActions, setRowActions] = useState({}); // { idx: "add" | "replace" | "skip" }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setFile(null);
    setPreview(null);
    setRowActions({});
    setError("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const submitFile = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/items/import/preview", fd);
      setPreview(data);
      // Default action: add for duplicates, (no action needed for new rows)
      const acts = {};
      data.rows.forEach((r, i) => {
        if (r.is_duplicate) acts[i] = "add";
      });
      setRowActions(acts);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const applyAll = async () => {
    setLoading(true);
    setError("");
    try {
      const body = {
        rows: preview.rows.map((r, i) => ({
          ...r.parsed,
          action: r.is_duplicate ? rowActions[i] || "add" : "add",
          expected_item_id: r.expected_item_id,
          expected_updated_at: r.expected_updated_at,
          expected_stock: r.expected_stock,
        })),
      };
      const { data } = await api.post("/items/import/apply", body);
      toast.success(
        `Import complete — ${data.created} new · ${data.added} added · ${data.replaced} replaced · ${data.skipped} skipped`
      );
      onImported?.();
      close();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    setError("");
    try {
      const res = await api.get("/items/template/xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "items_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const json = JSON.parse(text);
          setError(json.detail || "Download failed");
        } catch {
          setError("Download failed");
        }
      } else {
        setError(formatApiError(e));
      }
    }
  };

  if (!open) return null;

  const dupCount = preview?.rows?.filter((r) => r.is_duplicate).length || 0;
  const newCount = (preview?.rows?.length || 0) - dupCount;

  return (
    <Modal
      title={preview ? "Confirm Import" : "Import Inventory"}
      onClose={close}
      busy={loading}
      testid="import-dialog"
      size="lg"
    >
      <FormError>{error}</FormError>
      {!preview ? (
        /* Step 1 — file upload */
        <div className="space-y-5">
          <p className="text-sm text-zinc-600">
            Upload an Excel (.xlsx) file to import items. Use the template below to ensure
            the correct column names.
          </p>

          <Button type="button" variant="export"
            onClick={downloadTemplate}
            data-testid="import-template-btn"
          >
            ↓ Download Excel template
          </Button>

          <div>
            <div className="text-[10px] tracking-[0.1em] uppercase font-medium text-zinc-700 mb-1.5">
              Excel file (.xlsx)
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-zinc-600 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary-hover cursor-pointer"
              data-testid="import-file-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-zinc-200">
            <Button type="button" variant="cancel"
              onClick={close}
            >
              Cancel
            </Button>
            <Button type="button" variant="review"
              onClick={submitFile}
              disabled={!file}
              loading={loading}
              loadingLabel="Parsing…"
              data-testid="import-next-btn"
            >
              Preview →
            </Button>
          </div>
        </div>
      ) : (
        /* Step 2 — preview + resolve duplicates */
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-semantic-success border border-success/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-success font-medium">
                New
              </div>
              <div className="text-xl font-semibold tabular-nums text-semantic-success">{newCount}</div>
            </div>
            <div className="bg-semantic-warning border border-warning/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-warning font-medium">
                Duplicates
              </div>
              <div className="text-xl font-semibold tabular-nums text-semantic-warning">{dupCount}</div>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-zinc-500 font-medium">
                Total
              </div>
              <div className="text-xl font-semibold tabular-nums text-zinc-900">
                {preview.rows.length}
              </div>
            </div>
          </div>

          {dupCount > 0 && (
            <div className="flex items-start gap-2 bg-semantic-warning border border-warning/30 px-3 py-2.5 rounded-sm text-xs text-semantic-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Choose per-row action for duplicates. <b>Add</b> = increment stock.{" "}
                <b>Replace</b> = overwrite item & stock. <b>Skip</b> = leave untouched.
              </span>
            </div>
          )}

          <div className="border border-zinc-200 rounded-sm max-h-[400px] overflow-auto">
            <table className="wms-table">
              <thead className="sticky top-0 bg-zinc-50 z-10">
                <tr>
                  <th>Part #</th>
                  <th>Model</th>
                  <th>Name</th>
                  <th>Price</th>
                  {preview.warehouse_codes.map((c) => (
                    <th key={c} className="text-right">{c}</th>
                  ))}
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => {
                  const dup = r.is_duplicate;
                  return (
                    <tr key={i} data-testid={`import-row-${i}`}>
                    <td className="font-mono text-xs font-medium">
                        {r.parsed.part_number}
                      </td>
                      <td className="text-xs">{r.parsed.model || "—"}</td>
                      <td className="text-xs">{r.parsed.part_name}</td>
                      <td className="num text-xs">{formatIDR(r.parsed.price)}</td>
                      {preview.warehouse_codes.map((c) => (
                        <td key={c} className="num text-xs">
                          {r.parsed.stock[c] ?? 0}
                          {dup && r.existing_stock && (
                            <div className="text-[10px] text-zinc-400">
                              was: {r.existing_stock[c] ?? 0}
                            </div>
                          )}
                        </td>
                      ))}
                      <td>
                        {dup ? (
                          <PassiveBadge tone="warning">
                            DUPLICATE
                          </PassiveBadge>
                        ) : (
                          <PassiveBadge tone="success">
                            NEW
                          </PassiveBadge>
                        )}
                      </td>
                      <td>
                        {dup ? (
                          <select
                            value={rowActions[i] || "add"}
                            onChange={(e) =>
                              setRowActions({ ...rowActions, [i]: e.target.value })
                            }
                            className="text-xs border border-zinc-300 rounded-sm px-2 py-1 bg-white"
                            data-testid={`import-action-${i}`}
                          >
                            <option value="add">Add stock</option>
                            <option value="replace">Replace</option>
                            <option value="skip">Skip</option>
                          </select>
                        ) : (
                          <span className="text-xs text-zinc-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-zinc-200">
            <Button type="button" variant="navigation-back"
              onClick={() => {
                setPreview(null);
                setFile(null);
              }}
              data-testid="import-back-btn"
            >
              ← Back
            </Button>
            <Button type="button" variant="success"
              onClick={applyAll}
              disabled={preview.rows.length === 0}
              loading={loading}
              loadingLabel="Importing…"
              data-testid="import-apply-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {`Import ${preview.rows.length} rows`}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
